import React, { useState, useEffect, useMemo } from 'react';
import { useTareas } from '../hooks/useTareas';
import { usePagination } from '../hooks/usePagination';
import { Tarea, CreateTareaForm, UpdateTareaForm } from '../types';

interface FormErrors {
  nombre?: string;
  descripcion?: string;
}

const Tareas: React.FC = () => {
  const { 
    tareas, 
    isLoading, 
    isLoadingActivas,
    error,
    createTarea, 
    updateTarea, 
    deleteTarea,
    isCreating, 
    isUpdating, 
    isDeleting
  } = useTareas();
  
  const [showForm, setShowForm] = useState(false);
  const [editingTarea, setEditingTarea] = useState<Tarea | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActivo, setFilterActivo] = useState('todos');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState<CreateTareaForm>({
    nombre: '',
    descripcion: '',
  });

  // Limpiar mensajes de éxito después de 3 segundos
  useEffect(() => {
    if (showSuccessMessage) {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage]);

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 200) {
      errors.nombre = 'El nombre no puede exceder 200 caracteres';
    }

    if (formData.descripcion && formData.descripcion.length > 500) {
      errors.descripcion = 'La descripción no puede exceder 500 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtrar tareas según búsqueda y filtros (global, luego paginar)
  const filteredTareas = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return tareas.filter(tarea => {
      const matchesSearch = term === '' ||
        tarea.nombre.toLowerCase().includes(term) ||
        (tarea.descripcion?.toLowerCase().includes(term) ?? false);
      const matchesActivo = filterActivo === 'todos' || 
        (filterActivo === 'activos' && tarea.activo) ||
        (filterActivo === 'inactivos' && !tarea.activo);
      return matchesSearch && matchesActivo;
    });
  }, [tareas, searchTerm, filterActivo]);

  const { page, setPage, totalItems, totalPages, pageStart, pageEnd, pageItems, goPrevPage, goNextPage } = usePagination(filteredTareas);

  // Reset de página al cambiar filtros/búsqueda
  useEffect(() => {
    setPage(0);
  }, [searchTerm, filterActivo, setPage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      if (editingTarea) {
        const updateData: UpdateTareaForm = {
          ...formData,
          activo: editingTarea.activo
        };
        await updateTarea({ id: editingTarea.id, tarea: updateData });
        setSuccessMessage('Tarea actualizada exitosamente');
      } else {
        await createTarea(formData);
        setSuccessMessage('Tarea creada exitosamente');
      }
      setShowSuccessMessage(true);
      handleCancel();
    } catch (error) {
      // Los errores se manejan en el hook
    }
  };

  const initializeForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
    });
    setFormErrors({});
    setEditingTarea(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    initializeForm();
  };

  const handleEdit = (tarea: Tarea) => {
    setEditingTarea(tarea);
    setFormData({
      nombre: tarea.nombre,
      descripcion: tarea.descripcion || '',
    });
    setFormErrors({});
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta tarea? Esta acción no se puede deshacer.')) {
      try {
        await deleteTarea(id);
        setSuccessMessage('Tarea eliminada exitosamente');
        setShowSuccessMessage(true);
      } catch (error: any) {
        // Mostrar mensaje de error detallado
        const errorData = error?.response?.data?.errors || {};
        let errorMessage = '';
        let suggestion = '';
        
        if (errorData.TareaEnTipoArea) {
          errorMessage = errorData.TareaEnTipoArea[0];
          suggestion = '\n\nEsta tarea pertenece a un tipo de área. No se puede eliminar completamente, solo se puede excluir desde cada área individual.';
        } else if (errorData.TareaPersonalizadaEnUso) {
          errorMessage = errorData.TareaPersonalizadaEnUso[0];
          suggestion = '\n\nVa a Áreas → Gestionar Tareas y elimina esta tarea personalizada de las áreas mencionadas.';
        } else {
          errorMessage = error?.response?.data?.message || error?.message || 'Error al eliminar la tarea';
          suggestion = '\n\nVerifica que la tarea no esté en uso en el sistema.';
        }
        
        alert(`No se pudo eliminar la tarea:\n\n${errorMessage}${suggestion}`);
      }
    }
  };

  const handleToggleActivo = async (tarea: Tarea) => {
    try {
      const updateData: UpdateTareaForm = {
        nombre: tarea.nombre,
        descripcion: tarea.descripcion || '',
        activo: !tarea.activo,
      };
      await updateTarea({ id: tarea.id, tarea: updateData });
      setSuccessMessage(`Tarea ${!tarea.activo ? 'activada' : 'desactivada'} exitosamente`);
      setShowSuccessMessage(true);
    } catch (error) {
    }
  };

  const handleInputChange = (field: keyof CreateTareaForm, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // Asignaciones a tipos de área se gestionan desde la página TipoÁreas

  if (isLoading || isLoadingActivas) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando tareas...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error al cargar tareas</h3>
                <div className="mt-2 text-sm text-red-700">
                  {error.message || 'Ha ocurrido un error inesperado. Por favor, intenta de nuevo.'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2 sm:px-4 lg:px-8">
      <div className="py-6">
        {/* Mensaje de éxito */}
        {showSuccessMessage && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-900">Gestión de Tareas de Limpieza</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-1">
              Crea y edita tareas. Las asignaciones a tipos de área se gestionan en
              <a href="/tipo-areas" className="text-indigo-600 hover:underline"> Tipos de Áreas</a>.
            </p>
          </div>
          <button
            onClick={() => {
              initializeForm();
              setShowForm(true);
            }}
            className="bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
          >
            Nueva Tarea
          </button>
        </div>

        {/* Filtros y búsqueda */}
        <div className="bg-white shadow rounded-lg p-3 sm:p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Buscar
              </label>
              <input
                type="text"
                placeholder="Buscar por nombre o descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Estado
              </label>
              <select
                value={filterActivo}
                onChange={(e) => setFilterActivo(e.target.value)}
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm"
              >
                <option value="todos">Todas las tareas</option>
                <option value="activos">Solo activas</option>
                <option value="inactivos">Solo inactivas</option>
              </select>
            </div>
            <div className="flex items-end">
              <div className="text-xs sm:text-sm text-gray-600">
                {totalItems} tarea{totalItems !== 1 ? 's' : ''} encontrada{totalItems !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        {showForm && (
          <div className="bg-white shadow rounded-lg p-3 sm:p-6 mb-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingTarea ? 'Editar Tarea' : 'Nueva Tarea'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.nombre ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.nombre}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700">
                    Descripción
                  </label>
                  <textarea
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    rows={3}
                    className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-xs sm:text-sm ${
                      formErrors.descripcion ? 'border-red-300' : ''
                    }`}
                  />
                  {formErrors.descripcion && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.descripcion}</p>
                  )}
                </div>
              </div>

              {/* Asignaciones a tipos de área removidas de esta vista */}

              <div className="mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="order-2 sm:order-1 bg-gray-300 text-gray-700 px-4 py-3 w-full sm:w-auto rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isCreating || isUpdating}
                  className="order-1 sm:order-2 bg-indigo-600 text-white px-4 py-3 w-full sm:w-auto rounded-md hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                >
                  {isCreating || isUpdating ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </div>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {totalItems === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-500">
                {searchTerm || filterActivo !== 'todos' 
                  ? 'No se encontraron tareas que coincidan con los filtros.' 
                  : 'No hay tareas disponibles.'}
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {pageItems.map((tarea) => (
                <li key={tarea.id}>
                  <div className="px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className={`w-8 h-8 rounded-md flex items-center justify-center ${
                            tarea.activo ? 'bg-green-500' : 'bg-gray-400'
                          }`}>
                            <span className="text-white text-sm font-medium">
                              {tarea.nombre.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3 sm:ml-4">
                          <div className="text-sm sm:text-base font-medium text-gray-900">
                            {tarea.nombre}
                          </div>
                          {tarea.descripcion && (
                            <div className="text-xs sm:text-sm text-gray-500">
                              {tarea.descripcion}
                            </div>
                          )}
                          <div className="text-xs sm:text-sm text-gray-400">
                            Estado: {tarea.activo ? 'Activa' : 'Inactiva'} • 
                            Tipos de área: {tarea.tiposArea.length} • 
                            Creada: {new Date(tarea.fechaCreacion).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs sm:text-sm font-medium ${
                          tarea.activo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {tarea.activo ? 'Activa' : 'Inactiva'}
                        </span>
                      </div>
                    </div>
                    {tarea.tiposArea.length > 0 && (
                      <div className="mt-3">
                        <div className="text-xs sm:text-sm text-gray-500 mb-1">Tipos de área asociados:</div>
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                          {tarea.tiposArea.map((tipoArea) => (
                            <span
                              key={tipoArea.id}
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${
                                tipoArea.obligatoria 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}
                            >
                              {tipoArea.nombre} {tipoArea.obligatoria && '(Obligatoria)'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-xs sm:text-sm text-gray-500">
                        {tarea.fechaModificacion && (
                          <span>Modificada: {new Date(tarea.fechaModificacion).toLocaleDateString()}</span>
                        )}
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                        <button
                          onClick={() => handleToggleActivo(tarea)}
                          disabled={isUpdating}
                          className={`text-sm font-medium px-3 py-3 w-full sm:w-auto text-center rounded transition-colors ${
                            tarea.activo 
                              ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50' 
                              : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                          } disabled:opacity-50`}
                        >
                          {tarea.activo ? 'Desactivar' : 'Activar'}
                        </button>
                        <button
                          onClick={() => handleEdit(tarea)}
                          className="text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 text-sm font-medium px-3 py-3 w-full sm:w-auto text-center rounded transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDelete(tarea.id)}
                          disabled={isDeleting}
                          className="text-red-600 hover:text-red-900 hover:bg-red-50 text-sm font-medium px-3 py-3 w-full sm:w-auto text-center rounded transition-colors disabled:opacity-50"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Paginación */}
        {totalItems > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs sm:text-sm text-gray-600">
              Mostrando {pageStart + 1}–{pageEnd} de {totalItems}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={goPrevPage}
                disabled={page === 0}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página anterior"
              >
                ← Anterior
              </button>
              <span className="text-xs sm:text-sm text-gray-600">Página {page + 1} de {totalPages}</span>
              <button
                onClick={goNextPage}
                disabled={page >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40"
                aria-label="Página siguiente"
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Tareas; 